import React from 'react';
import { StyleSheet, View, Text, ScrollView, TouchableOpacity } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { spacing, typography } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../context/ThemeContext';
import { appConfig } from '../../config/appConfig';

type Props = NativeStackScreenProps<MainStackParamList, 'PrivacyPolicy'>;

const PrivacyPolicyScreen = ({ navigation }: Props) => {
  const { theme } = useTheme();
  
  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          Política de Privacidad
        </Text>
        <View style={styles.placeholder} />
      </View>
      
      <ScrollView style={styles.scrollView}>
        <View style={styles.content}>
          <Text style={[styles.title, { color: theme.colors.text }]}>
            Política de Privacidad de {appConfig.name}
          </Text>
          
          <Text style={[styles.updateDate, { color: theme.colors.grey.medium }]}>
            Última actualización: 20 de abril de 2025
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Esta Política de Privacidad describe cómo {appConfig.name} recopila, utiliza y comparte su información personal cuando utiliza nuestra aplicación.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            1. Información que Recopilamos
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Recopilamos la siguiente información cuando usted utiliza {appConfig.name}:
          </Text>

          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • <Text style={{ fontWeight: 'bold' }}>Información de la cuenta:</Text> nombre, apellido, correo electrónico, nombre de usuario y contraseña.
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • <Text style={{ fontWeight: 'bold' }}>Datos físicos:</Text> altura, peso, porcentaje de grasa corporal y grasa visceral (si los proporciona).
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • <Text style={{ fontWeight: 'bold' }}>Datos de entrenamiento:</Text> rutinas de ejercicio, series, repeticiones, pesos utilizados y progresión.
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • <Text style={{ fontWeight: 'bold' }}>Información de uso:</Text> cómo interactúa con la aplicación, qué funciones utiliza y con qué frecuencia.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            2. Cómo Utilizamos su Información
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Utilizamos la información recopilada para:
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • Proporcionar, mantener y mejorar nuestra aplicación.
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • Personalizar su experiencia según sus preferencias y datos físicos.
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • Permitirle compartir datos con amigos (según su configuración de privacidad).
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • Analizar tendencias de uso para mejorar la aplicación.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            3. Compartición de Datos
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Toda la información que se guarda en {appConfig.name} es de uso exclusivo para la aplicación. No vendemos ni compartimos su información personal con terceros, excepto en las siguientes circunstancias:
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • <Text style={{ fontWeight: 'bold' }}>Con su consentimiento:</Text> Compartimos sus datos físicos y de progreso con otros usuarios solo si usted lo permite explícitamente a través de la configuración de privacidad de la aplicación.
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • <Text style={{ fontWeight: 'bold' }}>Proveedores de servicios:</Text> Trabajamos con terceros que nos ayudan a proporcionar y mejorar nuestro servicio (por ejemplo, servidores de Firebase y servicios de Google).
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • <Text style={{ fontWeight: 'bold' }}>Google Analytics:</Text> Utilizamos Google Analytics para recopilar datos anónimos sobre el uso de la aplicación. Estos datos nos ayudan a entender cómo se utiliza la aplicación y a mejorar la experiencia del usuario.
          </Text>
          
          <Text style={[styles.bulletPoint, { color: theme.colors.text }]}>
            • <Text style={{ fontWeight: 'bold' }}>Requisitos legales:</Text> Podemos divulgar su información si estamos obligados legalmente a hacerlo.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            4. Seguridad de los Datos
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Tomamos medidas razonables para proteger su información personal contra pérdida, robo, uso indebido y acceso no autorizado. Sin embargo, ningún sistema de seguridad es impenetrable y no podemos garantizar la seguridad absoluta de su información personal.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            5. Sus Opciones y Derechos
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Puede acceder y actualizar su información personal a través de la configuración de su perfil en la aplicación. También puede ajustar su configuración de privacidad para controlar qué información se comparte con otros usuarios.
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Si desea eliminar su cuenta y todos sus datos, puede hacerlo desde la sección "Eliminar Cuenta" en la configuración de su perfil.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            6. Cambios a esta Política
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Podemos actualizar nuestra Política de Privacidad ocasionalmente. Le notificaremos cualquier cambio publicando la nueva Política de Privacidad en esta página y actualizando la fecha de "última actualización".
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Se le aconseja revisar esta Política de Privacidad periódicamente para cualquier cambio. Los cambios a esta Política de Privacidad son efectivos cuando se publican en esta página.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            7. Consentimiento
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Al utilizar nuestra aplicación, usted acepta nuestra Política de Privacidad. Si no está de acuerdo con esta política, por favor no utilice nuestra aplicación.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            8. Contacto
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Si tiene alguna pregunta sobre esta Política de Privacidad, por favor contáctenos a través de la información proporcionada en la aplicación.
          </Text>
          
          <View style={styles.footer}>
            <Text style={[styles.footerText, { color: theme.colors.grey.medium }]}>
              Desarrollado por: {appConfig.author}
            </Text>
            <Text style={[styles.footerText, { color: theme.colors.grey.medium }]}>
              {appConfig.copyrightText}
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  backButton: {
    padding: spacing.sm,
  },
  placeholder: {
    width: 24,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: spacing.lg,
    paddingBottom: spacing.xxl,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  updateDate: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.xl,
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  paragraph: {
    fontSize: typography.fontSize.md,
    lineHeight: 22,
    marginBottom: spacing.md,
  },
  bulletPoint: {
    fontSize: typography.fontSize.md,
    lineHeight: 22,
    marginBottom: spacing.xs,
    paddingLeft: spacing.md,
  },
  footer: {
    marginTop: spacing.xl,
    paddingTop: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
  },
  footerText: {
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
});

export default PrivacyPolicyScreen; 